import { Component, OnDestroy, OnInit, signal, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { OrderFlowAuthService } from './auth/order-flow-auth.service';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { HealthService } from './core/health.service';
import { AppUpdateService } from './core/app-update.service';
import { AuthenticationResult, ClientAuthError, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { NavMenuComponent } from './shell/nav-menu.component';
import { SecuritiesService } from './counterparty-data/securities/securities.service';
import { PortfoliosService } from './counterparty-data/portfolios/portfolios.service';
import { FooterComponent } from './core/footer';
import { SidebarComponent } from './shell/sidebar';
import { SignalRService } from './shared/access/signalr.service';

type LoadingStatus =
  'msal-init' | 'health-check' | 'loading-securities' | 'loading-portfolios' | 'not-logged-in' | 'error' | 'ready';

interface LoadingState {
  status: LoadingStatus;
  message: string;
  progress: number;
  errorMessage?: string;
  isAuthError?: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'of-root',
  imports: [NavMenuComponent, RouterOutlet, FooterComponent, SidebarComponent],
  template: `
    @if (isIframe) {
      <!-- Hidden MSAL iframe for silent token renewal — render nothing -->
    } @else if (loadingState().status !== 'ready') {
      <div class="of-splash">
        <img src="assets/cigp-logo.png" alt="Order Flow" class="of-splash__logo" />

        @if (loadingState().status === 'not-logged-in') {
          <p class="of-splash__tagline">CIGP Order Flow</p>
          <button class="btn btn-outline-primary rounded-2 mt-2" (click)="login()">
            <i class="bi bi-box-arrow-in-right me-2"></i>Sign in
          </button>
        } @else if (loadingState().status === 'error') {
          <div class="of-splash__error">
            <i class="bi bi-exclamation-triangle of-splash__error-icon"></i>
            <p class="of-splash__error-text">{{ loadingState().errorMessage }}</p>
            <p class="of-splash__error-contact">
              Contact <a href="mailto:it-support@cigp.com">it-support&#64;cigp.com</a> if the problem persists.
            </p>
            <button class="btn btn-outline-primary rounded-2" (click)="retryLoading()">
              <i class="bi bi-arrow-clockwise me-2"></i>Retry
            </button>
          </div>
        } @else {
          <div class="of-splash__progress-wrap">
            <div
              class="progress"
              role="progressbar"
              [attr.aria-valuenow]="loadingState().progress"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <div class="progress-bar" [style.width.%]="loadingState().progress"></div>
            </div>
          </div>
          <p class="of-splash__message">{{ loadingState().message }}</p>
        }
      </div>
    } @else {
      <div class="layout-wrapper">
        <of-nav-menu></of-nav-menu>
        <div class="d-flex flex-grow-1 flex-fill" style="flex: 1 1 auto; min-height: 0;">
          <of-sidebar></of-sidebar>
          <div class="content">
            <div class="router-outlet-panel">
              <router-outlet></router-outlet>
            </div>
          </div>
        </div>
      </div>
      <of-footer />
    }
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100vh;
        overflow: hidden;
      }

      /* ── Splash screen ── */
      .of-splash {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100vh;
        gap: 1.25rem;
      }

      .of-splash__logo {
        width: 72px;
        object-fit: contain;
        margin-bottom: 0.5rem;
      }

      .of-splash__tagline {
        margin: 0;
        font-size: 0.95rem;
        color: var(--bs-secondary-color);
      }

      .of-splash__progress-wrap {
        width: 360px;
      }

      .of-splash__progress-wrap .progress {
        border-radius: 4px;
        height: 4px;
      }

      .of-splash__message {
        margin: 0;
        font-size: 0.85rem;
        color: var(--bs-secondary-color);
      }

      .of-splash__error {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
        text-align: center;
        max-width: 400px;
      }

      .of-splash__error-icon {
        font-size: 2rem;
        color: var(--bs-warning);
      }

      .of-splash__error-text {
        margin: 0;
        font-size: 0.875rem;
        color: var(--bs-secondary-color);
      }

      .of-splash__error-contact {
        margin: 0.25rem 0 0;
        font-size: 0.78rem;
        color: var(--bs-secondary-color);

        a {
          color: var(--bs-primary);
          text-decoration: underline;
        }
      }

      /* ── Full app layout ── */
      .layout-wrapper {
        display: flex;
        flex-direction: column;
        height: 100vh;
        overflow: hidden;
      }

      .d-flex {
        display: flex;
        flex: 1 1 auto;
        min-height: 0;
      }

      .content {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        position: relative;
        max-height: calc(100vh - 80px);
      }

      .router-outlet-panel {
        border-radius: 0.18rem;
        margin: 0;
        min-height: 100%;
        padding: 0.34rem 0.34rem 0.34rem 0;
      }
    `
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private authService = inject(MsalService);
  orderFlowAuthService = inject(OrderFlowAuthService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private healthService = inject(HealthService);
  private router = inject(Router);
  private securitiesService = inject(SecuritiesService);
  private portfoliosService = inject(PortfoliosService);
  private signalRService = inject(SignalRService);
  private appUpdateService = inject(AppUpdateService);

  private readonly destroying$ = new Subject<void>();
  private msalInitDone = false;
  private wasUnavailable = false;

  public readonly isIframe = window !== window.parent && !window.opener;

  protected readonly loadingState = signal<LoadingState>({
    status: 'msal-init',
    message: 'Initializing...',
    progress: 10
  });

  ngOnInit(): void {
    this.appUpdateService.init();

    this.orderFlowAuthService.enableAccountStorageEvents();
    this.authService.handleRedirectObservable().subscribe({
      error: (err) => {
        // state_mismatch happens when stale MSAL entries in localStorage don't match the
        // state in the redirect response (e.g. after a partial login or cache corruption).
        // Clear the cache and retry once. The sessionStorage flag prevents an infinite loop
        // if clearing the cache doesn't resolve the issue (e.g. a browser extension keeps
        // wiping storage mid-flight).
        if (err instanceof ClientAuthError && err.errorCode === 'state_mismatch') {
          if (sessionStorage.getItem('of_auth_retry') === '1') {
            sessionStorage.removeItem('of_auth_retry');
            this.loadingState.set({
              status: 'error',
              message: '',
              progress: 0,
              isAuthError: true,
              errorMessage:
                'Sign-in failed due to a browser storage issue. Try clearing your browser data or use a private window.'
            });
            return;
          }
          sessionStorage.setItem('of_auth_retry', '1');
          this.authService.instance.clearCache().then(() => window.location.replace('/'));
        } else {
          // Any other redirect error (network failure, server error, etc.) — surface it
          // so the user isn't silently stuck on the initializing splash screen.
          this.loadingState.set({
            status: 'error',
            message: '',
            progress: 0,
            isAuthError: true,
            errorMessage: 'Sign-in failed. Please try again or contact support.'
          });
        }
      }
    });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED || msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
        if (this.orderFlowAuthService.getAllAccounts().length === 0) {
          this.authService.instance.clearCache().then(() => window.location.replace('/'));
        }
      });

    // After a successful login redirect, restart the loading sequence
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this.destroying$)
      )
      .subscribe((result: EventMessage) => {
        sessionStorage.removeItem('of_auth_retry');
        const payload = result.payload as AuthenticationResult;
        // payload.account can be null in some AAD flows (e.g. SSO, silent redirect).
        // setActiveAccount(null) is a no-op, so fall back to checkAndSetActiveAccount()
        // which picks up the account from getAllAccounts() if getActiveAccount() is still null.
        if (payload.account) {
          this.orderFlowAuthService.setActiveAccount(payload.account);
        }
        this.orderFlowAuthService.checkAndSetActiveAccount();
        this.orderFlowAuthService.loggedIn$.next(true);
        // Prevent the inProgress$ handler from calling runLoadAfterAuth() a second time
        // when it fires with InteractionStatus.None shortly after this LOGIN_SUCCESS event.
        this.msalInitDone = true;
        this.runLoadAfterAuth();
      });

    // Kick off the loading sequence the first time MSAL finishes processing
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
        if (this.msalInitDone) return;
        this.msalInitDone = true;

        if (this.orderFlowAuthService.checkAndSetActiveAccount()) {
          this.orderFlowAuthService.loggedIn$.next(true);
          this.runLoadAfterAuth();
        } else {
          this.loadingState.set({ status: 'not-logged-in', message: '', progress: 0 });
        }
      });
  }

  protected login(): void {
    this.orderFlowAuthService.login$().subscribe();
  }

  protected retryLoading(): void {
    if (this.loadingState().isAuthError) {
      sessionStorage.removeItem('of_auth_retry');
      this.authService.instance.clearCache().then(() => window.location.reload());
      return;
    }
    this.runLoadAfterAuth();
  }

  private runLoadAfterAuth(): void {
    this.loadingState.set({ status: 'health-check', message: 'Checking backend availability...', progress: 30 });

    this.healthService
      .isBackendAvailable$()
      .pipe(takeUntil(this.destroying$))
      .subscribe({
        next: (available) => {
          if (!available) {
            this.setError('The backend is currently unavailable. Please try again shortly.');
            return;
          }
          this.loadCoreData();
        },
        error: (err) => this.setError(err?.message ?? 'Failed to contact the backend.')
      });
  }

  private loadCoreData(): void {
    if (!this.orderFlowAuthService.canViewFundamentalData()) {
      this.finishLoading();
      return;
    }

    this.loadingState.set({ status: 'loading-securities', message: 'Loading securities...', progress: 60 });

    this.securitiesService
      .checkAndLoadSecuritiesIntoCache()
      .pipe(takeUntil(this.destroying$))
      .subscribe({
        next: () => this.loadPortfolios(),
        error: (err) => this.setError('Failed to load securities. ' + (err?.message ?? 'Please retry.'))
      });
  }

  private loadPortfolios(): void {
    this.loadingState.set({ status: 'loading-portfolios', message: 'Loading portfolios...', progress: 80 });

    this.portfoliosService
      .checkAndLoadPortfoliosIntoCache()
      .pipe(takeUntil(this.destroying$))
      .subscribe({
        next: () => this.finishLoading(),
        error: (err) => this.setError('Failed to load portfolios. ' + (err?.message ?? 'Please retry.'))
      });
  }

  private finishLoading(): void {
    this.loadingState.set({ status: 'ready', message: '', progress: 100 });
    this.appUpdateService.notifyAppReady();

    if (this.orderFlowAuthService.loggedIn$.value) {
      // LOGIN_SUCCESS fires while handleRedirectPromise is still running, so inProgress$
      // hasn't returned to None yet. Waiting here ensures the account is fully committed
      // to the MSAL cache before SignalR's accessTokenFactory makes its first token request.
      this.msalBroadcastService.inProgress$
        .pipe(
          filter((s) => s === InteractionStatus.None),
          take(1),
          takeUntil(this.destroying$)
        )
        .subscribe(() => this.signalRService.init());
    }

    // Periodic health polling now that app is fully loaded
    this.healthService
      .isBackendAvailableInterval$()
      .pipe(takeUntil(this.destroying$))
      .subscribe((isAvailable) => {
        if (!isAvailable) {
          this.wasUnavailable = true;
          this.router.navigate(['/unavailable']);
        } else if (this.wasUnavailable) {
          this.wasUnavailable = false;
          this.router.navigate(['/']);
        }
      });
  }

  private setError(message: string): void {
    this.loadingState.set({ status: 'error', message: '', progress: 0, errorMessage: message });
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }
}
